import { cn } from '@/lib/utils';

/** Superfície padrão do painel — alinhada ao dashboard (borda neutra + vidro escuro). */
export const painelSurfaceClass = cn(
  'rounded-2xl border border-white/10 bg-blue-950/45 shadow-lg shadow-black/25 backdrop-blur-md'
);

/** Blocos em destaque (welcome, ênfase leve). */
export const painelSurfaceAccentClass = cn(
  'rounded-2xl border border-sky-400/25 bg-blue-950/45 shadow-[0_0_44px_-14px_rgba(56,189,248,0.2)] backdrop-blur-md'
);

/** Tabelas e listas dentro de painel (mesma borda; `overflow-hidden` opcional no wrapper). */
export const painelTableShellClass = cn(painelSurfaceClass, 'overflow-hidden');

/** Cartões de ação / seções leves. */
export const painelInsetCardClass = cn(
  'rounded-2xl border border-white/10 bg-white/[0.04] shadow-lg shadow-black/20 backdrop-blur-sm'
);

/**
 * Descrições e subtítulos (cor neutra slate, alinhada à referência do painel).
 * Use com `font-inter` (System UI) + tamanho adequado (`text-sm`, `text-xs`, etc.).
 */
export const painelDescriptionClass = 'text-slate-400';

/** Texto auxiliar mais discreto (rodapés, dicas, metadata). */
export const painelDescriptionMutedClass = 'text-slate-400/75';

/** Toggle do painel — trilho azul escuro quando desligado, sky quando ligado. */
export const painelSwitchClass = cn(
  'border transition-[background-color,box-shadow,border-color]',
  'data-[state=unchecked]:border-sky-900/75 data-[state=unchecked]:bg-[#000b6a]',
  'data-[state=unchecked]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.45)]',
  'data-[state=checked]:border-sky-400/55 data-[state=checked]:bg-sky-500',
  'data-[state=checked]:shadow-[0_0_14px_-3px_rgba(56,189,248,0.65)]',
  'focus-visible:ring-2 focus-visible:ring-sky-400/45 focus-visible:ring-offset-0',
  '[&>span]:border [&>span]:border-sky-900/40 [&>span]:bg-sky-100',
  '[&>span]:shadow-[0_1px_4px_rgba(0,0,0,0.35)]',
  'data-[state=checked]:[&>span]:border-white/35 data-[state=checked]:[&>span]:bg-white'
);

/**
 * Botão « voltar » do topo — mesmo formato de antes (`rounded-xl`), com fundo sempre visível.
 * Usar com `Button variant="ghost" asChild`.
 */
export const painelPageBackButtonClass = cn(
  'mb-6 -ml-2 inline-flex h-10 shrink-0 items-center justify-center self-start rounded-xl px-4',
  'bg-white/10 text-sm font-medium text-slate-300',
  'transition-colors hover:!bg-white/[0.14] hover:!text-slate-100'
);

/**
 * Mesmo formato do voltar, para ações como « Ver no site » (sem deslocamento lateral).
 */
export const painelGhostNavButtonClass = cn(
  'inline-flex h-10 shrink-0 items-center justify-center rounded-xl px-4',
  'bg-white/10 text-sm font-medium text-slate-300',
  'transition-colors hover:!bg-white/[0.14] hover:!text-slate-100'
);

/* --- Workspace (boards, tarefas, modal): superfícies e controlos unificados --- */

/** Card dentro do board (arrastável). */
export const workspaceKanbanCardClass = cn(
  'w-full rounded-[10px] border border-white/[0.08] bg-gradient-to-b from-blue-950/70 to-blue-950/55 p-0 text-left',
  'shadow-[0_2px_8px_-2px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.04)]',
  'transition-all duration-300 ease-out',
  'hover:border-sky-400/25 hover:from-blue-950/85 hover:to-blue-950/65 hover:shadow-[0_12px_40px_-16px_rgba(0,0,0,0.55),0_0_0_1px_rgba(56,189,248,0.08)] hover:-translate-y-0.5',
  'active:translate-y-0 active:scale-[0.99] active:transition-[transform] active:duration-150',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/35',
  'cursor-grab active:cursor-grabbing'
);

/** Coluna do Kanban (lista de cards). */
export const workspaceKanbanColumnClass = cn(
  'flex min-h-[120px] flex-1 flex-col gap-[10px] rounded-[14px] px-2.5 py-2.5',
  'border border-white/[0.07] bg-blue-950/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-md'
);

/** Estado de drop / destaque na coluna. */
export const workspaceKanbanColumnActiveClass = cn(
  'border-sky-400/35 bg-blue-950/55 shadow-[0_0_40px_-10px_rgba(56,189,248,0.28),inset_0_0_0_1px_rgba(56,189,248,0.12)] ring-1 ring-sky-400/30'
);

/** Faixa sempre visível atrás de « Adicionar card ». */
export const workspaceKanbanAddCardSlotClass = cn(
  'mt-1 rounded-lg border border-white/10 bg-white/[0.07] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
);

/** Botão interno da faixa « Adicionar card ». */
export const workspaceKanbanAddCardButtonClass = cn(
  'flex w-full items-center gap-2 rounded-[inherit] py-2.5 pl-2.5 pr-3 text-left',
  'font-inter text-sm text-slate-400 transition-colors',
  'hover:bg-white/[0.08] hover:text-slate-100'
);

/** Cartão de conteúdo (bugs, roadmap, feedback, tarefas em modo cartões). */
export const workspaceContentCardClass = cn(
  'rounded-2xl border border-white/10 bg-blue-950/45 p-5 backdrop-blur-md transition-all duration-300'
);

/** Inputs, selects e textareas no workspace. */
export const workspaceControlClass = cn(
  'border border-white/10 bg-white/[0.06] text-slate-100 placeholder:text-slate-500'
);

/** Conteúdo de Select / menus suspensos (workspace).
 * Portal sai do `.painel-root`, por isso forçamos cores escuras (evita `bg-accent` claro + texto branco). */
export const workspaceSelectContentClass = cn(
  'z-[200] overflow-hidden rounded-xl border border-sky-400/25 bg-gradient-to-b from-[#0b1638] to-[#070d2b] text-slate-100',
  'shadow-[0_20px_48px_-16px_rgba(0,0,0,0.75),0_0_0_1px_rgba(56,189,248,0.06)] backdrop-blur-none',
  '[&_[data-radix-select-viewport]]:p-1.5',
  // Itens — tipografia slate; hover/highlight azul escuro (sem bg-accent claro do shadcn)
  '[&_[role=option]]:cursor-pointer [&_[role=option]]:rounded-lg [&_[role=option]]:py-2 [&_[role=option]]:font-inter [&_[role=option]]:text-sm',
  '[&_[role=option]]:text-slate-200 [&_[role=option]]:bg-transparent [&_[role=option]]:outline-none [&_[role=option]]:transition-colors',
  '[&_[role=option]:hover]:!bg-[#0a1848] [&_[role=option]:hover]:!text-sky-50',
  '[&_[role=option][data-highlighted]]:!bg-[#0a1848] [&_[role=option][data-highlighted]]:!text-sky-50',
  '[&_[role=option]:focus]:!bg-[#0a1848] [&_[role=option]:focus]:!text-sky-50',
  '[&_[role=option][data-state=checked]]:font-medium [&_[role=option][data-state=checked]]:text-sky-200',
  '[&_[role=option][data-disabled]]:pointer-events-none [&_[role=option][data-disabled]]:opacity-45',
  '[&_[role=option]_svg]:text-sky-400'
);

/** Caixas internas no modal de tarefa (descrição, checklist, comentários). */
export const workspaceModalInsetClass = cn(
  'rounded-[10px] border border-sky-400/10 bg-blue-950/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
);

/** Shell do Dialog de detalhe da tarefa — alinhado ao azul do painel (#030023 / blue-950). */
export const workspaceModalShellClass = cn(
  'border-sky-400/20 bg-gradient-to-b from-blue-950/95 via-[#0b1428] to-blue-950/90 text-slate-200/95',
  'shadow-[0_0_0_1px_rgba(56,189,248,0.1),0_28px_64px_-20px_rgba(0,0,0,0.65)]',
  'backdrop-blur-xl'
);

/** Coluna principal (detalhe) dentro do modal. */
export const workspaceModalMainColumnClass = cn(
  'min-h-0 border-sky-400/10 bg-blue-950/25 lg:border-r lg:border-sky-400/10'
);

/** Coluna lateral — comentários (tom diferente do conteúdo). */
export const workspaceModalAsideClass = cn(
  'border-sky-400/10 bg-gradient-to-b from-[#060d18] via-indigo-950/40 to-[#040814]',
  'shadow-[inset_1px_0_0_rgba(56,189,248,0.08)]'
);

/** Select aberto dentro do modal do workspace (z acima do Dialog; fundo sólido — evita blur “estourado”). */
export const workspaceModalSelectContentClass = cn(
  workspaceSelectContentClass,
  'z-[250] max-h-72'
);

/** Botões da toolbar tipo Trello no modal. */
export const workspaceTrelloToolbarButtonClass = cn(
  'group/tool inline-flex items-center gap-2 rounded-lg border border-white/[0.07] bg-gradient-to-b from-white/[0.07] to-white/[0.03]',
  'px-3 py-2 font-inter text-[11px] font-medium tracking-wide text-slate-300',
  'shadow-[0_1px_0_rgba(255,255,255,0.04)] transition-all duration-200',
  'hover:border-sky-400/30 hover:text-white hover:shadow-[0_0_20px_-6px_rgba(56,189,248,0.35)] hover:-translate-y-px',
  'active:translate-y-0 active:scale-[0.98]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/30'
);

/** Toolbar Trello — estado ativo (mock). */
export const workspaceTrelloToolbarButtonActiveClass = cn(
  'border-sky-400/40 text-sky-100 shadow-[0_0_24px_-8px_rgba(56,189,248,0.45)] bg-sky-500/15'
);

/** Painel de vidro do workspace (overview, atividade) — alinhado a `painelSurfaceClass`. */
export const workspaceGlassPanelShellClass = cn(
  painelSurfaceClass,
  'shadow-[0_0_48px_-16px_rgba(56,189,248,0.18)]'
);

/** Select / Dropdown em qualquer página do painel (mesmo tom do workspace). */
export const painelSelectContentClass = workspaceSelectContentClass;

/** Shell dos modais de permissões — mesmo azul do painel (#030023 / blue-950). */
export const painelPermissionsModalShellClass = cn(
  'overflow-hidden rounded-2xl border border-white/10 text-slate-100',
  'bg-gradient-to-b from-blue-950/95 via-[#030023] to-blue-950/95',
  'shadow-[0_0_0_1px_rgba(56,189,248,0.1),0_28px_64px_-20px_rgba(0,0,0,0.65)]',
  'backdrop-blur-xl'
);

/** Item de permissão ativo dentro dos modais. */
export const painelPermissionItemEnabledClass = cn(
  'border-sky-400/25 bg-blue-950/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
);

/** Item de permissão inativo dentro dos modais. */
export const painelPermissionItemDisabledClass = cn(
  'border-white/10 bg-blue-950/30 opacity-65'
);

/** Badge/contador nos modais de permissões. */
export const painelPermissionCounterClass = cn(
  'rounded-xl border border-sky-400/25 bg-blue-950/50 text-sky-200'
);

/** Área rolável dos modais — preferir `PainelRocketScroll` / `RocketScrollArea`. */
export const painelPermissionsModalScrollClass = cn(
  'scroll-smooth',
  '[scrollbar-width:none]',
  '[-ms-overflow-style:none]',
  '[&::-webkit-scrollbar]:hidden'
);

/** Superfície de AlertDialog, painel médio e popovers “cheios”. */
export const painelDialogSurfaceClass = cn(
  'border border-white/10 bg-blue-950/95 text-slate-100 shadow-2xl shadow-black/60 backdrop-blur-xl'
);

/** Botão primário do painel (ação principal / guardar). */
export const painelButtonPrimaryClass = cn(
  'rounded-xl border border-sky-400/40 bg-sky-500/90 font-poppins font-semibold text-white shadow-lg shadow-sky-950/30',
  'hover:border-sky-300 hover:bg-sky-400 hover:text-white',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-0'
);

/** Botão outline no tema escuro do painel. */
export const painelButtonOutlineClass = cn(
  'rounded-xl border border-white/15 bg-white/[0.04] font-poppins text-blue-100 shadow-none',
  'hover:border-sky-400/25 hover:bg-white/10 hover:text-white',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/30 focus-visible:ring-offset-0'
);

/** Botão secundário (ações auxiliares, ex.: Buscar ID). */
export const painelButtonSecondaryClass = cn(
  'rounded-xl border border-sky-400/35 bg-sky-500/15 font-inter text-sm font-medium text-sky-100 shadow-none',
  'hover:border-sky-400/55 hover:bg-sky-500/25 hover:text-white',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/30 focus-visible:ring-offset-0',
  'disabled:opacity-50'
);

/** Botão ghost no tema escuro do painel. */
export const painelButtonGhostClass = cn(
  'rounded-xl text-slate-300',
  'hover:bg-white/10 hover:text-white',
  'focus-visible:ring-2 focus-visible:ring-sky-400/30 focus-visible:ring-offset-0'
);

/** Botão destrutivo no painel. */
export const painelButtonDestructiveClass = cn(
  'rounded-xl bg-rose-600 font-poppins font-semibold text-white',
  'hover:bg-rose-500 hover:text-white',
  'focus-visible:ring-2 focus-visible:ring-rose-400/35 focus-visible:ring-offset-0'
);

/**
 * Neutraliza o anel padrão do shadcn Button (`ring-ring` / `ring-offset-background`).
 * Usar em modais do painel com `variant="ghost"`.
 */
export const painelButtonResetRingClass =
  'ring-0 ring-offset-0 focus-visible:ring-2 focus-visible:ring-sky-400/25 focus-visible:ring-offset-0';

/** Cancelar em modais — sem contorno branco, alinhado ao tema escuro. */
export const painelModalCancelButtonClass = cn(
  painelButtonResetRingClass,
  'h-10 rounded-xl border border-white/10 bg-white/[0.03] px-4 font-inter text-sm font-medium text-slate-400 shadow-none',
  'hover:border-white/15 hover:bg-white/[0.07] hover:text-slate-200'
);

/** Ação auxiliar em modais (ex.: Buscar ID) — preenchido sky, sem contorno claro. */
export const painelModalLookupButtonClass = cn(
  painelButtonResetRingClass,
  'h-10 shrink-0 gap-1.5 rounded-xl border-0 bg-sky-600 px-4 font-poppins text-xs font-semibold tracking-wide text-white shadow-none',
  'hover:bg-sky-500 hover:text-white',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 focus-visible:ring-offset-0',
  'disabled:pointer-events-none disabled:opacity-50',
  'shadow-[0_0_28px_-8px_rgba(14,165,233,0.55)]'
);
